<script>
    export let data, monitoring_results, notices, violations, geography, cities;
    // export let selSystem;
    import { selSystem } from '../store.js';
    import Map from './Map.svelte';
    import MonitoringResults from './MonitoringResults.svelte';
    import NegativeSymbol from './NegativeSymbol.svelte';
    import PositiveSymbol from './PositiveSymbol.svelte';
    import Notices from './Notices.svelte';

    let systemData,
        monitoringResults,
        noticeResults,
        violationResults,
        systemCoordinates;

    $: if($selSystem) {
        systemData = data.find(function(d){ return d.pwsid == $selSystem; });
        monitoringResults = monitoring_results.filter(function(d){ return d.pwsid == $selSystem });
        noticeResults = notices.filter(function(d){ return d.pwsid == $selSystem });
        violationResults = violations.filter(function(d){ return d.pwsid == $selSystem });
        systemCoordinates = [systemData.longitude_measure, systemData.latitude_measure];
    }

</script>

<svg style="position: absolute;">
    <defs>
        <pattern id="hatch" 
            width="5px"
            height="5px"
            patternUnits="userSpaceOnUse"
            patternContentUnits="userSpaceOnUse"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            patternTransform="rotate(45)"
        >
        <line
            x1="0"
            x2="100"
            y1="50"
            y2="50"
            stroke-width="10"
            stroke="#C0C0C0"
        />
        <line
            x1="50"
            x2="50"
            y1="0"
            y2="100"
            stroke-width="10"
            stroke="#C0C0C0"
        />
        </pattern>
    </defs>
</svg>

{#if $selSystem}
    <div class="system-name">{systemData.system_name}</div>
    <div class="county-name">SERVES {systemData.population.toLocaleString()} PEOPLE IN {systemData.county.toUpperCase()} COUNTY</div>
    <div class="row top">
        <div class="detail-group">
                {#if systemData.current_failing == "Failing"}
                    <h3 class="highlight negative"><NegativeSymbol/>Failing</h3>
                    <p class="text">Failing to meet safe drinking water standards for
                        <span class="standards">
                            {#if systemData.water_quality_pct > 0.20}<span class="standard">water quality</span>{/if}
                            {#if systemData.affordability_pct > 0.20}<span class="standard">affordability</span>{/if}
                            {#if systemData.accessibility_pct > 0.20}<span class="standard">accessibility</span>{/if}
                        </span>
                        {#if systemData.primary_source == 'SWP' | systemData.primary_source == 'GWP' | systemData.primary_source == 'GUP'}
                        It is purchasing water from an alternative source.
                        {:else}
                            {#if systemData.arsenic_treated >= 1 | systemData.nitrate_treated >= 1 | systemData.tcp_treated >= 1}
                            <span class="standards">
                                This system is treating the water for 
                                {#if systemData.arsenic_treated >= 1}<span class="standard">arsenic</span>{/if}
                                {#if systemData.nitrate_treated >= 1}<span class="standard">nitrate</span>{/if}
                                {#if systemData.tcp_treated >= 1}<span class="standard">1,2,3-TCP</span>{/if}
                            </span>
                            {:else}
                            This provider is not treating its water and it is not purchasing water from an alternative source.
                            {/if}
                        {/if}
                    </p>
                {:else}
                    <h3 class="highlight positive"><PositiveSymbol/>Not failing</h3>
                    <p class="text">Not failing to meet safe drinking water standards.
                    {#if systemData.final_safer_status != "Not Assessed" & systemData.final_safer_status != "Not At-Risk"}
                    It is {systemData.risk_assessment_result.toLowerCase().replace("-", " ")} of future failure.
                    {/if}
                    {#if systemData.primary_source == 'SWP' | systemData.primary_source == 'GWP' | systemData.primary_source == 'GUP'}
                    It is purchasing water from an alternate source.
                    {:else}
                        {#if systemData.arsenic_treated >= 1 | systemData.nitrate_treated >= 1 | systemData.tcp_treated >= 1}
                        <span class="standards">
                            This system is treating the water for 
                            {#if systemData.arsenic_treated >= 1}<span class="standard">arsenic</span>{/if}
                            {#if systemData.nitrate_treated >= 1}<span class="standard">nitrate</span>{/if}
                            {#if systemData.tcp_treated >= 1}<span class="standard">1,2,3-TCP</span>{/if}
                        </span>
                        {:else}
                        This provider is not treating its water and it is not purchasing water from an alternate source.
                        {/if}
                    {/if}
                    </p>
                {/if}
            <div>
                {#if systemData.primary_mcl_violation == "YES"}
                    <h3 class="highlight negative"><NegativeSymbol/>Current violations</h3>
                    <p class="text">Exceeded the maximum contaminant level for 
                        {systemData.primary_analytes.replace(";", ",").toLowerCase()}.
                        {#if systemData.bottled_water_or_hauled_water_reliance_threshold_met == "1 or more occurrence(s) of bottled/hauled water reliance within the last 3 years"}
                            This system has relied on bottled or hauled water at least once in the last three years.
                        {/if}
                    </p>
                {:else}
                    <h3 class="highlight positive"><PositiveSymbol/>No current violations</h3>
                    <p class="text">It does not currently have violations for exceeding the maximum level for any contaminants.
                        {#if systemData.bottled_water_or_hauled_water_reliance_threshold_met == "1 or more occurrence(s) of bottled/hauled water reliance within the last 3 years"}
                        This provider has relied on bottled or hauled water at least once in the last three years.
                    {/if}
                    </p>
                {/if}
            </div>
            {#if noticeResults.length > 0}
                <h3 class="highlight negative"><NegativeSymbol/>Contaminated water notices</h3>
                <p class="text">
                    This provider has sent {noticeResults.length} notices of contaminated water in the last three years.
                </p>
                <Notices {noticeResults}/>
            {:else}
                <h3 class="highlight positive"><PositiveSymbol/>No contaminated water notices</h3>
                <p class="text">This provider has not sent any notices of contaminated water in the last three years.</p>
            {/if}
        </div>
        <Map {geography} {cities} {systemCoordinates}/>
    </div>
    <MonitoringResults {monitoringResults} {systemData}/>
    <h3 style="margin-top: 2em; margin-bottom: 0.2em;">Contact information</h3>
    {#if (systemData.admin_phone || systemData.admin_email) && (systemData.district_phone || systemData.district_email)}
        <p class="text"><strong>System administrative contact:</strong>
            {#if systemData.admin_phone != '' && systemData.admin_email != ''}
                {systemData.admin_phone}, {systemData.admin_email}
            {:else if systemData.admin_phone != ''}
                {systemData.admin_phone}
            {:else if systemData.admin_email != ''}
                {systemData.admin_email}
            {:else}
                Contact information is not available.
            {/if}
        </p>
        <p class="text"><strong>District contact:</strong>
            {#if systemData.district_phone != '' && systemData.district_email != ''}
                {systemData.district_phone}, {systemData.district_email}
            {:else if systemData.district_phone != ''}
                {systemData.district_phone}
            {:else if systemData.district_email != ''}
                {systemData.district_email}
            {:else}
                Contact information is not available.
            {/if}
        </p>
    {:else}
        <p class="text">Contact information for this system and district is not available.</p>
    {/if}
    <div class="note">The charts above show average test results for each year. Data includes results from active sources that tested above the reporting level. High monitoring results indicate potential exposure and do not necessarily mean that delivered water is contaminated at that level.</div>
{/if}
